import { useAuth } from 'src/auth/AuthContext';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  TextField,
  Typography,
  useMediaQuery,
  Box,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  MailOutline,
  ChevronLeft
} from '@mui/icons-material';
import {
  doSignInWithEmailAndPassword,
  doSignInWithGoogle,
  sendPasswordReset,
  doCreateUserWithEmailAndPassword,
  doUpdateProfile,
  formatAuthError
} from '../../utils/auth';

type LoginView = 'signin' | 'register' | 'forgot' | 'sent';

const logoWhiteUrl = 'https://assets.brewprks.com/media/images/logo-white.svg';
const markWhiteUrl = 'https://assets.brewprks.com/media/images/mark-white.svg';

const headingFont = "'Bricolage Grotesque', sans-serif";
const bodyFont = "'Instrument Sans', system-ui, sans-serif";

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 48,
    borderRadius: '10px',
    backgroundColor: '#fff',
    fontFamily: bodyFont,
    fontSize: 15,
    color: '#17222C',
    '& fieldset': { borderColor: '#CBD8E1' },
    '&:hover fieldset': { borderColor: '#CBD8E1' },
    '&.Mui-focused fieldset': { borderColor: '#17222C', borderWidth: '2px' }
  },
  '& input::placeholder': { color: '#94A2AE', opacity: 1 }
};

const primaryButtonSx = {
  height: 48,
  borderRadius: '10px',
  backgroundColor: '#1778A8',
  color: '#fff',
  fontFamily: bodyFont,
  fontWeight: 600,
  fontSize: 15,
  textTransform: 'none',
  boxShadow: 'none',
  gap: '10px',
  '&:hover': { backgroundColor: '#0E4F70', boxShadow: 'none' }
};

const outlinedButtonSx = {
  height: 48,
  borderRadius: '10px',
  border: '1px solid #CBD8E1',
  backgroundColor: '#fff',
  color: '#17222C',
  fontFamily: bodyFont,
  fontWeight: 500,
  fontSize: 15,
  textTransform: 'none',
  gap: '10px',
  '&:hover': { backgroundColor: '#EEF2F5', border: '1px solid #CBD8E1' }
};

const linkSx = {
  fontFamily: bodyFont,
  fontWeight: 600,
  color: '#0E4F70',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': { color: '#0A3C52' }
};

const FieldLabel: React.FC<{ htmlFor: string; children: React.ReactNode }> = ({
  htmlFor,
  children
}) => (
  <Typography
    component='label'
    htmlFor={htmlFor}
    sx={{ fontFamily: bodyFont, fontWeight: 500, fontSize: 14, color: '#17222C' }}
  >
    {children}
  </Typography>
);

const GoogleGlyph = () => (
  <svg width='18' height='18' viewBox='0 0 48 48' aria-hidden='true'>
    <path
      fill='#EA4335'
      d='M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z'
    />
    <path
      fill='#4285F4'
      d='M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 2.9-2.2 5.4-4.7 7.1l7.6 5.9c4.4-4.1 6.9-10.1 6.9-17.5z'
    />
    <path
      fill='#FBBC05'
      d='M10.5 28.6c-.5-1.4-.8-3-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.8-6.1z'
    />
    <path
      fill='#34A853'
      d='M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.9 2.3-8.3 2.3-6.2 0-11.5-4.2-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z'
    />
  </svg>
);

const Login: React.FC = () => {
  const navigate = useNavigate();
  const loggedIn = useAuth().loggedIn;

  const [view, setView] = useState<LoginView>('signin');
  const [isSigningIn, setIsSigningIn] = useState<boolean>(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [resent, setResent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const wide = useMediaQuery('(min-width:900px)');

  useEffect(() => {
    if (view === 'signin' && isSigningIn && loggedIn) {
      setIsSigningIn(false);
      navigate('/locations');
    }
  }, [loggedIn, isSigningIn, view, navigate]);

  // Registration mirrors the sign-in flow above: navigate only once the auth
  // listener has finished /auth/me and set the user, so the protected
  // /create-location route doesn't bounce a fresh signup back to /login.
  useEffect(() => {
    if (view === 'register' && isRegistering && loggedIn) {
      setIsRegistering(false);
      navigate('/create-location');
    }
  }, [loggedIn, isRegistering, view, navigate]);

  const goTo = (next: LoginView) => {
    setView(next);
    setError('');
    setResent(false);
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (view === 'register' && password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (view === 'register') {
      if (!firstName.trim() || !lastName.trim()) {
        setError('Please enter your first and last name');
        return;
      }
    }

    try {
      if (view === 'signin') {
        if (!isSigningIn) {
          setIsSigningIn(true);
          await doSignInWithEmailAndPassword(email, password);
        }
      } else {
        if (!isRegistering) {
          setIsRegistering(true);
          const userCredential = await doCreateUserWithEmailAndPassword(
            email,
            password
          );
          const displayName = `${firstName} ${lastName}`.trim();
          await doUpdateProfile(userCredential.user, displayName, null);
        }
      }
    } catch (error) {
      setIsSigningIn(false);
      setIsRegistering(false);
      setError(formatAuthError(error));
    }
  };

  const handleGoogleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (view === 'signin') {
        if (!isSigningIn) {
          setIsSigningIn(true);
          await doSignInWithGoogle();
        }
      } else {
        if (!isRegistering) {
          setIsRegistering(true);
          await doSignInWithGoogle();
        }
      }
    } catch (error) {
      setIsSigningIn(false);
      setIsRegistering(false);
      setError(formatAuthError(error));
    }
  };

  const handleSendReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      setResetSending(true);
      await sendPasswordReset(email);
      setResetSending(false);
      setView('sent');
    } catch (error) {
      setResetSending(false);
      setError(formatAuthError(error));
    }
  };

  const handleResend = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await sendPasswordReset(email);
      setResent(true);
    } catch (error) {
      setError(formatAuthError(error));
    }
  };

  const busy = isSigningIn || isRegistering;

  const errorBanner = error && (
    <Box
      role='alert'
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        p: '12px 14px',
        borderRadius: '10px',
        backgroundColor: '#FDECEC',
        border: '1px solid #F4C7C7',
        fontFamily: bodyFont,
        fontSize: 14,
        lineHeight: 1.4,
        color: '#8A1F1F'
      }}
    >
      <svg
        width='18'
        height='18'
        viewBox='0 0 24 24'
        fill='none'
        stroke='#B42318'
        strokeWidth='2'
        style={{ flex: 'none', marginTop: 1 }}
      >
        <circle cx='12' cy='12' r='9' />
        <path d='M12 8v5M12 16.5v.01' />
      </svg>
      <span>{error}</span>
    </Box>
  );

  const orDivider = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontFamily: bodyFont,
        fontSize: 13,
        color: '#7C8893'
      }}
    >
      <Box sx={{ flex: 1, height: '1px', backgroundColor: '#D7E1E8' }} />
      or
      <Box sx={{ flex: 1, height: '1px', backgroundColor: '#D7E1E8' }} />
    </Box>
  );

  const googleButton = (
    <Button fullWidth onClick={handleGoogleAuth} sx={outlinedButtonSx}>
      <GoogleGlyph />
      Continue with Google
    </Button>
  );

  const backToSignIn = (
    <Link
      component='button'
      type='button'
      onClick={() => goTo('signin')}
      sx={{
        ...linkSx,
        fontWeight: 500,
        fontSize: 14,
        color: '#5F6B76',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        alignSelf: 'flex-start',
        '&:hover': { color: '#17222C' }
      }}
    >
      <ChevronLeft sx={{ fontSize: 18 }} />
      Back to sign in
    </Link>
  );

  const passwordToggle = (show: boolean, toggle: () => void) => (
    <InputAdornment position='end'>
      <IconButton
        aria-label={show ? 'Hide password' : 'Show password'}
        onClick={toggle}
        edge='end'
        sx={{ color: '#5F6B76', '&:hover': { backgroundColor: '#EEF2F5' } }}
      >
        {show ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexWrap: 'wrap' }}>
      {/* Brand panel */}
      <Box
        sx={{
          flex: '5 1 420px',
          backgroundColor: '#1778A8',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          gap: '40px',
          p: { xs: '32px 32px', sm: '48px 56px' }
        }}
      >
        <Box
          component='img'
          src={markWhiteUrl}
          alt=''
          sx={{
            position: 'absolute',
            right: -140,
            bottom: -120,
            width: 620,
            opacity: 0.14,
            pointerEvents: 'none'
          }}
        />
        <Box
          component='img'
          src={logoWhiteUrl}
          alt='Brewprks'
          sx={{ width: 168, position: 'relative' }}
        />
        {wide && (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxWidth: 400
            }}
          >
            <Typography
              sx={{
                fontFamily: bodyFont,
                fontWeight: 600,
                fontSize: 13,
                lineHeight: 1,
                letterSpacing: '.12em',
                textTransform: 'uppercase',
                color: '#fff'
              }}
            >
              Merchant portal
            </Typography>
            <Typography
              sx={{
                fontFamily: headingFont,
                fontWeight: 700,
                fontSize: 52,
                lineHeight: 1.02,
                letterSpacing: '-.02em',
                color: '#fff'
              }}
            >
              Run your loyalty program from one place.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: '6 1 480px',
          backgroundColor: '#F4F7F9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: '56px 24px'
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          {view === 'signin' && (
            <Box
              component='form'
              onSubmit={handleEmailAuth}
              sx={{ display: 'flex', flexDirection: 'column', gap: '28px' }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  component='h1'
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 700,
                    fontSize: 32,
                    lineHeight: 1.1,
                    letterSpacing: '-.02em',
                    color: '#17222C'
                  }}
                >
                  Sign in
                </Typography>
                <Typography
                  sx={{ fontFamily: bodyFont, fontSize: 15, lineHeight: 1.4, color: '#5F6B76' }}
                >
                  Manage locations, plans and rewards.
                </Typography>
              </Box>

              {errorBanner}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <FieldLabel htmlFor='email'>Email</FieldLabel>
                  <TextField
                    id='email'
                    type='email'
                    autoComplete='email'
                    placeholder='you@business.com'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={busy}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline'
                    }}
                  >
                    <FieldLabel htmlFor='password'>Password</FieldLabel>
                    <Link
                      component='button'
                      type='button'
                      onClick={() => goTo('forgot')}
                      sx={{ ...linkSx, fontWeight: 500, fontSize: 13 }}
                    >
                      Forgot password?
                    </Link>
                  </Box>
                  <TextField
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={busy}
                    sx={fieldSx}
                    InputProps={{
                      endAdornment: passwordToggle(showPassword, () =>
                        setShowPassword(!showPassword)
                      )
                    }}
                  />
                </Box>
                <Button type='submit' fullWidth disabled={busy} sx={{ ...primaryButtonSx, mt: '4px' }}>
                  {isSigningIn && <CircularProgress size={16} sx={{ color: '#fff' }} />}
                  {isSigningIn ? 'Signing in…' : 'Sign in'}
                </Button>
              </Box>

              {orDivider}
              {googleButton}

              <Typography
                sx={{ fontFamily: bodyFont, fontSize: 14, color: '#5F6B76', textAlign: 'center' }}
              >
                New to Brewprks?{' '}
                <Link component='button' type='button' onClick={() => goTo('register')} sx={linkSx}>
                  Create an account
                </Link>
              </Typography>
            </Box>
          )}

          {view === 'register' && (
            <Box
              component='form'
              onSubmit={handleEmailAuth}
              sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              {backToSignIn}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  component='h1'
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 700,
                    fontSize: 32,
                    lineHeight: 1.1,
                    letterSpacing: '-.02em',
                    color: '#17222C'
                  }}
                >
                  Create an account
                </Typography>
                <Typography
                  sx={{ fontFamily: bodyFont, fontSize: 15, lineHeight: 1.45, color: '#5F6B76' }}
                >
                  Enter your personal details below. You'll set up your store
                  information in the next step.
                </Typography>
              </Box>

              {errorBanner}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                <Box sx={{ display: 'flex', gap: '12px' }}>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '7px' }}>
                    <FieldLabel htmlFor='firstName'>First name</FieldLabel>
                    <TextField
                      id='firstName'
                      autoComplete='given-name'
                      value={firstName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setFirstName(e.target.value)
                      }
                      required
                      disabled={busy}
                      sx={fieldSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '7px' }}>
                    <FieldLabel htmlFor='lastName'>Last name</FieldLabel>
                    <TextField
                      id='lastName'
                      autoComplete='family-name'
                      value={lastName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setLastName(e.target.value)
                      }
                      required
                      disabled={busy}
                      sx={fieldSx}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <FieldLabel htmlFor='registerEmail'>Email</FieldLabel>
                  <TextField
                    id='registerEmail'
                    type='email'
                    autoComplete='email'
                    placeholder='you@business.com'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={busy}
                    sx={fieldSx}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <FieldLabel htmlFor='registerPassword'>Password</FieldLabel>
                  <TextField
                    id='registerPassword'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='new-password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={busy}
                    sx={fieldSx}
                    InputProps={{
                      endAdornment: passwordToggle(showPassword, () =>
                        setShowPassword(!showPassword)
                      )
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                  <FieldLabel htmlFor='confirmPassword'>Confirm password</FieldLabel>
                  <TextField
                    id='confirmPassword'
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete='new-password'
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={busy}
                    sx={{
                      ...fieldSx,
                      ...(confirmPassword && password !== confirmPassword && {
                        '& .MuiOutlinedInput-root fieldset': {
                          borderColor: '#B42318'
                        }
                      })
                    }}
                    InputProps={{
                      endAdornment: passwordToggle(showConfirmPassword, () =>
                        setShowConfirmPassword(!showConfirmPassword)
                      )
                    }}
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <Typography sx={{ fontFamily: bodyFont, fontSize: 13, color: '#B42318' }}>
                      Passwords don't match.
                    </Typography>
                  )}
                </Box>
                <Button type='submit' fullWidth disabled={busy} sx={{ ...primaryButtonSx, mt: '4px' }}>
                  {isRegistering && <CircularProgress size={16} sx={{ color: '#fff' }} />}
                  {isRegistering ? 'Creating account…' : 'Create account'}
                </Button>
              </Box>

              {orDivider}
              {googleButton}
            </Box>
          )}

          {view === 'forgot' && (
            <Box
              component='form'
              onSubmit={handleSendReset}
              sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              {backToSignIn}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  component='h1'
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 700,
                    fontSize: 32,
                    lineHeight: 1.1,
                    letterSpacing: '-.02em',
                    color: '#17222C'
                  }}
                >
                  Reset your password
                </Typography>
                <Typography
                  sx={{ fontFamily: bodyFont, fontSize: 15, lineHeight: 1.45, color: '#5F6B76' }}
                >
                  Enter the email you use for Brewprks and we'll send you a link
                  to set a new password.
                </Typography>
              </Box>

              {errorBanner}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                <FieldLabel htmlFor='resetEmail'>Email</FieldLabel>
                <TextField
                  id='resetEmail'
                  type='email'
                  autoComplete='email'
                  placeholder='you@business.com'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={resetSending}
                  sx={fieldSx}
                />
              </Box>
              <Button type='submit' fullWidth disabled={resetSending} sx={primaryButtonSx}>
                {resetSending && <CircularProgress size={16} sx={{ color: '#fff' }} />}
                {resetSending ? 'Sending…' : 'Send reset link'}
              </Button>
            </Box>
          )}

          {view === 'sent' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '14px',
                  backgroundColor: '#EAF6FC',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <MailOutline sx={{ color: '#0E4F70', fontSize: 26 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  component='h1'
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 700,
                    fontSize: 32,
                    lineHeight: 1.1,
                    letterSpacing: '-.02em',
                    color: '#17222C'
                  }}
                >
                  Check your email
                </Typography>
                <Typography
                  sx={{ fontFamily: bodyFont, fontSize: 15, lineHeight: 1.45, color: '#5F6B76' }}
                >
                  We sent a reset link to{' '}
                  <Box component='span' sx={{ color: '#17222C', fontWeight: 600 }}>
                    {email}
                  </Box>
                  .
                </Typography>
              </Box>

              {errorBanner}

              <Button fullWidth onClick={() => goTo('signin')} sx={outlinedButtonSx}>
                Back to sign in
              </Button>
              <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: '#5F6B76' }}>
                Didn't get it?{' '}
                <Link component='button' type='button' onClick={handleResend} sx={linkSx}>
                  {resent ? 'Sent again' : 'Resend'}
                </Link>
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
